import { Box, Collapse } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  btnType,
  colors,
  fontWeight,
  Text,
  textLevel,
  Badge,
  badgeType,
  badgeSize,
  badgeStyle,
} from '../../../../../packages';
import { Icons, IconButton } from '../../../../../packages';
import { sxStyles, useStyles } from './ExerciseDetail.styles';
import { useNavigate } from 'react-router-dom';
import {
  getExpandedEvaluation,
  getAppointmentEvaluationProgress,
} from '../../../../../services/conversation/conversation.service';
import { getAppointmentById } from '../../../../../services/appointment/appointment.service';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProfileInfo } from '../../../../../packages/ui/templates/profile-info';
import { ExerciseSteps } from './components';
import { conversationActionCreators } from '../../../../../redux/modules/conversation/actions';
import { formatTheoryName } from '../../../../../utils/CommonUtils';

const ExerciseDetail = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [exerciseDetail, setExerciseDetail] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [summarySessionInstructions, setSummarySessionInstructions] = useState(true);
  const [isAssigned, setIsAssigned] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleUnassignEvaluation = async () => {
    if (appointmentId && evaluationId) {
      dispatch(
        conversationActionCreators.deleteEvaluation({
          appointmentId,
          evaluationId,
          isExercise: true,
        }),
      );
      setIsAssigned(false);
    }
  };

  const handleAssignEvaluation = async () => {
    if (appointmentId && evaluationId) {
      dispatch(
        conversationActionCreators.addAssignedEvaluation({
          evaluationIds: [evaluationId],
          appointmentId,
        }),
      );
      setIsAssigned(true);
    }
  };

  const { appointmentId, evaluationId } = useParams();

  const fetchExerciseDetail = async () => {
    try {
      setLoading(true);
      const response = await getExpandedEvaluation(appointmentId, evaluationId, true);
      if (response?.status === 200) {
        setExerciseDetail(response?.data?.evaluation);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching exercise detail:', error);
      setExerciseDetail(null);
      setLoading(false);
    }
  };

  const fetchAppointment = async () => {
    try {
      const response = await getAppointmentById({ appointmentId });
      if (response?.status === 200) {
        setAppointment(response?.data);
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      setAppointment(null);
    }
  };

  const fetchEvaluationProgress = async () => {
    try {
      const response = await getAppointmentEvaluationProgress({ appointmentId });
      if (response?.status === 200) {
        const exerciseList = response?.data.filter(evaluation => evaluation.type === 'EXERCISE') || [];
        const currentExerciseAssigned = exerciseList.some(
          exercise => exercise.id === evaluationId && !exercise.isRecommended,
        );
        setIsAssigned(currentExerciseAssigned);
      }
    } catch (error) {
      console.error('Error fetching evaluation progress:', error);
      setIsAssigned(false);
    }
  };

  useEffect(() => {
    void fetchExerciseDetail();
    void fetchAppointment();
  }, [appointmentId, evaluationId]);

  useEffect(() => {
    void fetchEvaluationProgress();
  }, []);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh" gap="8px">
        <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
          Loading...
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column">
      <Box sx={sxStyles.header}>
        <Box display="flex" alignItems="center" gap="16px">
          <Button variant={btnType.OUTLINE} className={classes.btnBack} onClick={handleGoBack}>
            <Icons color={colors.primary500} glyph="arrow-left" width="20px" height="20px" />
          </Button>
          <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
            Exercise details
          </Text>
        </Box>
        <Box>
          <IconButton
            variant={btnType.OUTLINE}
            sx={isAssigned ? sxStyles.outlineBtn : sxStyles.assignBtn}
            onClick={isAssigned ? handleUnassignEvaluation : handleAssignEvaluation}
          >
            <Text
              level={textLevel.S}
              weight={fontWeight.BOLD}
              color={isAssigned ? colors.destructive600 : colors.primary600}
            >
              {isAssigned ? 'Unassign' : 'Assign'}
            </Text>
          </IconButton>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap="40px" padding="40px">
        <Box display="flex" flexDirection="column" gap="32px">
          <Box display="flex" flexDirection="column" gap="24px">
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" flexDirection="column" gap="8px">
                <Box display="flex" alignItems="center" gap="12px">
                  <Text level={textLevel.L} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    {exerciseDetail?.name}
                  </Text>
                  <Badge
                    variant={badgeType.FILLED}
                    size={badgeSize.XSMALL}
                    style={isAssigned ? badgeStyle.PRIMARY_ACTIVE : badgeStyle.PRIMARY}
                  >
                    <Text
                      level={textLevel.XS}
                      weight={fontWeight.SEMI_BOLD}
                      color={isAssigned ? colors.white : colors.primary600}
                    >
                      {isAssigned ? 'Assigned' : 'Recommended'}
                    </Text>
                  </Badge>
                </Box>
                <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral500}>
                  {formatTheoryName(exerciseDetail?.meta?.theory)} {isAssigned ? '• Assigned on ' : ''}
                  {isAssigned ? dayjs(exerciseDetail?._id?.date).format('MMMM D, YYYY') : ''}
                </Text>
              </Box>
              <Box>
                <ProfileInfo
                  type="provider"
                  isProvider={true}
                  photo={appointment?.practitionerImage || ''}
                  fullName={appointment?.practitionerName || 'Provider'}
                  profilePhotoName={appointment?.practitionerName || 'Provider'}
                  role={appointment?.practitionerDesignation || 'Provider'}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap="12px">
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                {exerciseDetail?.description}
              </Text>
            </Box>
          </Box>
          {/* <Box display="flex" flexDirection="column" gap="8px">
            <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
              Cultural considerations:
            </Text>
            <Box display="flex" flexDirection="column" gap="8px" paddingLeft="32px">
              <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
                {exerciseDetail?.meta?.culturalConsiderations}
              </Text>
            </Box>
          </Box> */}
        </Box>
        <Box>
          <>
            <Box className={classes.section}>
              <Box
                className={classes.sectionHeader}
                onClick={() => setSummarySessionInstructions(!summarySessionInstructions)}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    transform: summarySessionInstructions ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <Icons glyph="chevron-down" color={colors.neutral700} />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                    Summary Session Instructions
                  </Text>
                </Box>
              </Box>

              <Collapse in={summarySessionInstructions}>
                <Box display="flex" flexDirection="column" paddingLeft="32px" paddingTop="16px">
                  <ExerciseSteps exerciseSteps={exerciseDetail?.contentBlocks || []} />
                </Box>
              </Collapse>
            </Box>
            <Box className={classes.divider} />
          </>
        </Box>
      </Box>
    </Box>
  );
};

export default ExerciseDetail;
